/**
 * Chat Panel - Internal Communication sidebar for supplier detail.
 * Mirrors legacy right-hand panel in "Communication audit log" tab.
 * Shows messages between customer and supplier, with send capability.
 */

import { useEffect, useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, Send, MessageSquare, User } from 'lucide-react';
import {
  suppliersAPI, coreAPI,
  type SupplierProfile, type SupplierMessage, type SupplierCustomerRelationship,
} from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

interface ChatPanelProps {
  supplier: SupplierProfile;
}

export function ChatPanel({ supplier }: ChatPanelProps) {
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [customers, setCustomers] = useState<SupplierCustomerRelationship[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [messages, setMessages] = useState<SupplierMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');

  const currentUserId = user?.id;

  // Fetch customer list for this supplier
  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier.company]);

  // Fetch messages when customer changes
  useEffect(() => {
    if (selectedCustomer) {
      fetchMessages();
    } else {
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const res = await coreAPI.getSupplierRelationships({
        supplier: supplier.company,
        is_active: true,
        page_size: 100,
      });
      const rels = res.data.results || [];
      setCustomers(rels);
      // Auto-select first customer (or the user's own company)
      if (rels.length > 0) {
        const userCompany = user?.company;
        const match = rels.find((r) => r.customer === userCompany);
        setSelectedCustomer(match ? match.customer : rels[0].customer);
      }
    } catch (err: unknown) {
      console.error('Failed to fetch customer relationships:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!selectedCustomer) return;
    try {
      setMessagesLoading(true);
      const res = await suppliersAPI.getMessages({
        supplier: supplier.company,
        customer: selectedCustomer,
        page_size: 100,
      });
      setMessages(res.data.results || []);
    } catch (err: unknown) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedCustomer) return;
    try {
      setSending(true);
      const res = await suppliersAPI.sendMessage({
        supplier: supplier.company,
        customer: selectedCustomer,
        content: newMessage.trim(),
      });
      setMessages((prev) => [...prev, res.data]);
      setNewMessage('');
    } catch (err: unknown) {
      console.error('Failed to send message:', err);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-50" />
        <p className="text-sm">No customer relationships found for this supplier.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header + Customer Selector */}
      <div className="pb-3 border-b">
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Internal Communication
        </h3>
        {customers.length > 1 && (
          <select
            value={selectedCustomer || ''}
            onChange={(e) => setSelectedCustomer(Number(e.target.value))}
            className="w-full text-sm border rounded-md px-2 py-1.5 bg-background"
          >
            {customers.map((rel) => (
              <option key={rel.customer} value={rel.customer}>
                {rel.customer_name}
              </option>
            ))}
          </select>
        )}
        {customers.length === 1 && (
          <Badge variant="outline" className="text-xs">
            {customers[0].customer_name}
          </Badge>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-3 space-y-3 min-h-[300px] max-h-[500px]">
        {messagesLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <p>No messages yet. Start the conversation.</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.sender === currentUserId;
            return (
              <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  {!isMe && <User className="h-3 w-3 text-muted-foreground" />}
                  <span className="text-xs font-medium">
                    {isMe ? 'You' : msg.sender_name}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {formatTime(msg.created_at)}
                  </span>
                </div>
                <div
                  className={`rounded-lg px-3 py-2 max-w-[85%] text-sm ${
                    isMe
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t pt-3">
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          rows={2}
          maxLength={2000}
          className="w-full text-sm border rounded-md px-3 py-2 resize-none bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        <div className="flex justify-end mt-2">
          <Button
            size="sm"
            disabled={!newMessage.trim() || sending}
            onClick={handleSend}
          >
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
